'use strict';

const { configuratorCtas } = require('../configuratorCtas');
const { changeCentreCtas } = require('../changeCentreCtas');

/**
 * @file singleTabConfigurator.js
 * @description
 * This module manages tab-specific behavior for a single-page configurator application.
 * It ensures that only one active tab can interact with critical CTAs (Call-To-Actions) at a time,
 * preventing conflicts from multiple open tabs. It handles:
 *
 * - Main vs. duplicate tab detection using `ecux.detectTabMgr`
 * - Conditional display of UI elements based on tab status
 * - Debug visual indicators in the document title
 * - Inter-tab communication via `storage` events
 * - Initialization of tab-related event listeners and CTA selectors
 *
 * Intended for use in environments where session integrity and single-tab enforcement are critical,
 * such as e-commerce configurators or order management flows.
 */
const singleTabConfigurator = {
    /**
     * Visible nav CTAs that isolation-off duplicate-tab must disable (native href + proxy).
     *
     * @readonly
     */
    NAV_DUPLICATE_TAB_CTAS: [
        '.configurator-navigation .btn-summary',
        '.configurator-navigation .btn-next',
        '.configurator-navigation .btn-order-online',
        '.list-group-item-action[data-view="order-online"]'
    ],

    isSummaryStage: function () {
        const params = new URLSearchParams(window.location.search);
        const shouldShow = params.get("showSummary") == true;
        return shouldShow;
    },
    updateDebugVisuals: function (prefix) {
        if (window.ecux.debug.isDebugEnabled()) {
            const original = document.title.replace(/^\s*\[[MD]\]\s*/i, '');
            document.title = `${prefix} ${original}`;
        };
    },
    showBlockedNotification: function () {
        if (window.ecux.detectTabMgr.getTabList().length > 1) {
            window.ecux.notify("We are unable to proceed with your request as there is an existing order open in another window. Please close the other window and try again.");
        }
    },

    /**
     * Isolation-off Order Online uses native `href`; `disabled` on `<a>` does not stop navigation.
     *
     * @private
     *
     * @param {MouseEvent} event
     */
    onNavDuplicateTabClick: function (event) {
        var button = event.target.closest(this.NAV_DUPLICATE_TAB_CTAS.join(', '));

        if (!button) {
            return;
        }

        if (!button.classList.contains('duplicate-tab-blocker')) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        this.showBlockedNotification();
    },

    allowCTA: function (allow) {
        function allowLocationCTA(allow) {
            const editLocationCTAs = document.querySelectorAll('.hide-edit-icon');
            editLocationCTAs.forEach(function (editLocationCTA) {
                if (allow) {
                    editLocationCTA.classList.remove('d-none');
                } else {
                    editLocationCTA.classList.add('d-none')
                }
            });
        }

        allowLocationCTA(allow);
        changeCentreCtas.patch({ duplicateTab: !allow });
    },
    tabOnLoadValidation: function () {
        if (window.ecux.detectTabMgr.isMainTab() || window.ecux.detectTabMgr.getTabList().length <= 1) {
            this.allowCTA(true);
            this.updateDebugVisuals('[M]');
        } else {
            this.updateDebugVisuals('[D]');
            this.allowCTA(false);
        }
    },
    setupPageListeners: function () {
        const updateDebugVisuals = this.updateDebugVisuals;
        const allowCTA = this.allowCTA;
        const reapplyOrderNowDisabled = configuratorCtas.reapplyOrderNowDisabled.bind(configuratorCtas);

        window.addEventListener('ecux.detectTabMgr.mainTab', function () {
            updateDebugVisuals('[M]');
            reapplyOrderNowDisabled();
        });

        window.addEventListener('ecux.detectTabMgr.duplicateTab', function () {
            updateDebugVisuals('[D]');
            allowCTA(false);
            ecux.detectTabMgr.setButtonState(false);
        });

        window.addEventListener('ecux.detectTabMgr.mainTabChanged', function () {
            if (ecux.detectTabMgr.isMainTab()) {
                updateDebugVisuals('[M]');
                allowCTA(true);
                reapplyOrderNowDisabled();
            }
        });

        window.addEventListener('storage', function (e) {
            if (e.key === 'syncTabRefresh' && e.newValue !== e.oldValue) {
                $('body').spinner().start();
                window.location.reload();
            }
        });

        document.addEventListener('click', this.onNavDuplicateTabClick.bind(this), true);
    },
    initTabEvents: function () {
        const pageCTASelectors = [
            '#configurator-footer-actions-summary',
            '#configurator-footer-actions-summary-m',
            '#configurator-footer-actions-checkout',
            '.recommendation-tile__action-input',
            '.configurator-footer-change-location-button',
            '.hide-edit-icon'
        ].concat(this.NAV_DUPLICATE_TAB_CTAS);

        if (!this.isSummaryStage()) {
            pageCTASelectors.push('#configuratorSummary-tab');
            pageCTASelectors.push('#configuratorSummary-tab-proxy');
            pageCTASelectors.push('#configuratorOrder-tab');
        }

        window.ecux.detectTabMgr.init(pageCTASelectors);
    },
    init: function () {
        this.initTabEvents();
        this.setupPageListeners();
        this.tabOnLoadValidation();
        configuratorCtas.reapplyOrderNowDisabled();
        setInterval(this.tabOnLoadValidation(), 15000);
    }
};

module.exports = singleTabConfigurator;
